import { useLayoutEffect, useMemo } from 'react';

import axios, { AxiosRequestConfig } from 'axios';
import { Navigate, Outlet } from 'react-router-dom';

import { useCurrentSpaceId, useGetSpaces, useSpaceCredits } from '@/entities/teamspace';
import { useCurrentUser } from '@/entities/user/currentUser';

import { PAYMENT_REQUIRED_STATUS_CODE } from '@/shared/api/errors';
import { PATHS } from '@/shared/constants/routes/paths';

const SPACE_NOT_FOUND_ERROR = 'You have no access to this resource';

export const SpaceGuard = () => {
  const { data: spaces, refetch: refetchSpaces } = useGetSpaces();
  const { refetch: refetchCredits } = useSpaceCredits();
  const spaceId = useCurrentSpaceId();
  const user = useCurrentUser((u) => u);

  useLayoutEffect(() => {
    const isSpaceIdPresent = (config: AxiosRequestConfig) => {
      const params = (config.params ?? {}) as Record<string, unknown>;
      return Boolean(
        'space_id' in params ||
          (config.url && new URL(config.url, config.baseURL || window.location.origin).searchParams.has('space_id')),
      );
    };

    const id = axios.interceptors.request.use((config) => {
      if (spaceId && !isSpaceIdPresent(config)) {
        const url = new URL(config.url || '', config.baseURL || window.location.origin);
        if (!url.pathname.startsWith('/api')) return config;
        url.searchParams.set('space_id', spaceId);
        config.url = url.toString();
      }
      return config;
    });

    return () => {
      axios.interceptors.request.eject(id);
    };
  }, [spaceId]);

  useLayoutEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      async (error) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const err = error.data?.error;
        if (typeof err === 'string' && err.startsWith(SPACE_NOT_FOUND_ERROR)) await refetchSpaces();

        if (error.response?.status === PAYMENT_REQUIRED_STATUS_CODE) void refetchCredits();

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(id);
    };
  }, [refetchCredits, refetchSpaces]);

  const needRedirect = useMemo(() => {
    if (!spaces || !user.data || !spaceId) return true;

    if (user.data.is_superuser) return false;

    const space = spaces.find((s) => s.id === spaceId);

    if (!space || (!space.is_active && user.data.id !== space.owner_id)) return true;

    return false;
  }, [spaceId, spaces, user]);

  if (needRedirect) return <Navigate to={PATHS.homepage} replace />;

  return <Outlet />;
};
